import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { useSharedWithMe } from "../../api/documents";
import { cx } from "../../lib/cx";

/**
 * "Shared with me": the documents the caller holds a direct grant on
 * (`userMemberships.list`). The endpoint returns the documents alongside the
 * memberships, so this section needs no second call.
 */
export function SharedSection({ enabled }: { enabled: boolean }): ReactNode {
  const shared = useSharedWithMe(enabled);
  const documents = shared.data?.documents ?? [];
  if (!enabled || shared.isPending || documents.length === 0) return null;

  return (
    <section aria-labelledby="sidebar-shared">
      <h2
        id="sidebar-shared"
        className="mb-1 px-2 text-xs font-semibold tracking-wide text-muted uppercase"
      >
        Shared with me
      </h2>
      <ul className="flex flex-col gap-0.5">
        {documents.map((document) => (
          <li key={document.id}>
            <NavLink
              to={document.url}
              className={({ isActive }) =>
                cx(
                  "flex items-center gap-2 rounded-md px-2 py-1 text-sm",
                  isActive
                    ? "bg-hover font-medium text-ink"
                    : "text-muted hover:bg-hover hover:text-ink",
                )
              }
            >
              <span aria-hidden="true" className="w-4 shrink-0 text-center">
                {document.icon ?? "📄"}
              </span>
              <span className="truncate">
                {document.title === "" ? "Untitled" : document.title}
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
    </section>
  );
}
